import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Container,
  Sidebar,
  TemplateContainer,
  TemplateGrid,
  CategoryList,
  SectionTitle,
  Header,
  TemplatesGrid,
  UploadButtonStyles,
  ErrorPageContainer,
  CategoryHeader,
  AddCategoryButton,
} from "../StyledComponents";
import {
  AddCategoryIcon,
  ErrorPageIcon,
  NoTemplatesIcon,
  UploadIcon,
} from "../../../assets/icons";
import { Button } from "@mui/material";
import ErrorToast from "../../../components/Notification/ErrorToast";
import PrintPreview from "../../../components/PrintPreview/PrintPreview";
import Loader from "../../../components/Loader/Loader";
import {
  isFileTypeAllowed,
  allowedExtensions,
  allowedTypes,
} from "../../../utils/fileValidation";
import TemplateCardItem from "../../../components/TemplateCard/TemplateCardItem";
import {
  addNewCategory,
  updateCategory,
  deleteCategory,
  fetchFileTemplates,
  fetchPackages,
  createPackage,
  updatePackage,
  deletePackage as deletePackageApi,
  saveSignTemplateMappingRules,
  mergePackage,
  combinePackageForSigning,
  renameGeneratedDocument,
  deleteGeneratedDocument,
  downloadTemplateWithMappings,
} from "../../../service/settings";
import { uploadTemplateApi } from "../../../service/matter";
import AddCategoryModal from "../../../components/AddCategoryModal/AddCategoryModal";
import { Dropdown, Modal, Segmented, Select, Tooltip } from "antd";
import {
  SIGNATURE_FILTERS,
  countSignable,
  filterTemplatesBySignature,
} from "../../../components/TemplateCard/templateSignature";
import {
  handleCreateCategoryUtil,
  handleUpdateCategoryUtil,
  handleDeleteCategoryUtil,
  handleDeleteTemplateUtil,
  handleMoveTemplateCategoryUtil,
  handleDisableTemplateUtil as handleDisableTemplateClick,
  handlePreviewUtil,
} from "./FileTemplateUtil";
import EmptyState from "../../../components/EmptyState";
import UploadTemplateModal from "./UploadTemplateModal";
import PackageModal from "./PackageModal";
import PackageGenerateModal from "./PackageGenerateModal";
import PackageResultsModal from "./PackageResultsModal";
import { parseSignTemplateMappingApiErrors } from "./signTemplateMappingHelpers";
import { downloadDocumentFile } from "../../../components/Document/DocumentUtils";
import styled from "styled-components";

const ViewToggle = styled.div`
  display: flex;
  gap: 0;
  margin-bottom: 0;
`;

const ToggleBtn = styled.button`
  padding: 6px 16px;
  font-size: 13px;
  font-weight: 600;
  border: 1px solid #d1d5db;
  background: ${(p) => (p.$active ? "#0C53BB" : "#fff")};
  color: ${(p) => (p.$active ? "#fff" : "#374151")};
  cursor: pointer;
  &:first-child {
    border-radius: 6px 0 0 6px;
  }
  &:last-child {
    border-radius: 0 6px 6px 0;
    border-left: none;
  }
  &:hover {
    opacity: 0.9;
  }
`;

const CreatePackageButtonStyles = {
  backgroundColor: "#111111",
  color: "#ffffff",
  borderRadius: "4px",
  boxShadow: "none",
  textTransform: "none",
  fontSize: "12px",
  fontWeight: 600,
  minHeight: "32px",
  padding: "6px 14px",
  "&:hover": {
    backgroundColor: "#000000",
    boxShadow: "none",
  },
};

const PackageList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PackageCard = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px 12px;
  padding-right: 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  box-sizing: border-box;
  min-height: 48px;
  min-width: 0;
  transition: background 0.15s, border-color 0.15s;
  &:hover {
    background: #f9fafb;
    border-color: #d1d5db;
  }
`;

const FolderGlyph = styled.span`
  position: relative;
  width: 24px;
  height: 18px;
  border-radius: 4px;
  background: #e0edff;
  border: 1px solid #bfdbfe;
  flex: 0 0 auto;
  &:before {
    content: "";
    position: absolute;
    left: 2px;
    top: -5px;
    width: 10px;
    height: 6px;
    border-radius: 4px 4px 0 0;
    background: #dbeafe;
    border: 1px solid #bfdbfe;
    border-bottom: 0;
  }
`;

const PackageText = styled.div`
  min-width: 0;
  flex: 1;
`;

const PackageName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PackageMeta = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const PackageMenuButton = styled.button`
  width: 30px;
  height: 30px;
  flex: 0 0 30px;
  margin-left: auto;
  margin-right: 8px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  background: #fff;
  color: #374151;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  &:hover {
    background: #f3f4f6;
  }
`;

const MenuDots = styled.span`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  span {
    width: 3px;
    height: 3px;
    border-radius: 999px;
    background: #374151;
  }
`;

const getUploadErrorMessage = (err) => {
  const data = err?.response?.data || {};
  return (
    data.details ||
    data.detail ||
    data.message ||
    data.error ||
    "File upload failed. Please try again."
  );
};

const getPackageErrorMessage = (err) => {
  const data = err?.response?.data || {};
  return (
    data.details ||
    data.detail ||
    data.message ||
    data.error ||
    "Failed to generate package."
  );
};

const getDocumentId = (doc) => doc?.id || doc?.document_id;

const FileTemplates = () => {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");

  const [templatesByCategory, setTemplatesByCategory] = useState({
    ["GENERAL"]: {
      active: [],
      disabled: [],
    },
  });

  const [templatePreview, setTemplatePreview] = useState({
    file_content: "",
    file_type: "",
    content_type: "",
    filename: "",
  });

  const [uploadTemplate, setUploadTemplate] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [toastMessages, setToastMessages] = useState([]);
  const [showToast, setShowToast] = useState(false);
  const [isLoading, setisLoading] = useState(false);
  const [openPreview, setOpenPreview] = useState(false);
  const [showErrorPage, setShowErrorPage] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [addCategoryModal, setAddCategoryModal] = useState(false);
  const [categoryModalMode, setCategoryModalMode] = useState("add");
  const [categoryName, setCategoryName] = useState("");
  const [error, setError] = useState("");
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [deleteConfirmTemplate, setDeleteConfirmTemplate] = useState(null);
  const [deleteCategoryModal, setDeleteCategoryModal] = useState(null);
  const [moveCategoryModal, setMoveCategoryModal] = useState(null);
  const [moveDestinationKey, setMoveDestinationKey] = useState("");

  // -- Packages state --
  const [viewMode, setViewMode] = useState("templates"); // "templates" | "packages"
  const [packages, setPackages] = useState([]);
  const [packageModalOpen, setPackageModalOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [packageSaving, setPackageSaving] = useState(false);
  const [deleteConfirmPackage, setDeleteConfirmPackage] = useState(null);
  const [allActiveTemplates, setAllActiveTemplates] = useState([]);
  const [generatePackageModalOpen, setGeneratePackageModalOpen] = useState(false);
  const [packageToGenerate, setPackageToGenerate] = useState(null);
  const [packageGenerating, setPackageGenerating] = useState(false);
  const [packageMergeResult, setPackageMergeResult] = useState(null);
  const [packageActionBusy, setPackageActionBusy] = useState(false);

  const inputRef = useRef(null);

  const acceptList = [...allowedTypes, ...allowedExtensions].join(",");

  const categoryActiveTemplates = templatesByCategory[selectedCategory]?.active || [];
  const categoryDisabledTemplates =
    templatesByCategory[selectedCategory]?.disabled || [];
  // "Signable" = the template can go out for e-signature as generated.
  const [signatureFilter, setSignatureFilter] = useState("all");
  const activeTemplates = filterTemplatesBySignature(categoryActiveTemplates, signatureFilter);
  const disabledTemplates = filterTemplatesBySignature(categoryDisabledTemplates, signatureFilter);
  const totalTemplates = categoryActiveTemplates.length + categoryDisabledTemplates.length;
  const signableCount = countSignable(categoryActiveTemplates) + countSignable(categoryDisabledTemplates);
  const signatureFilterOptions = SIGNATURE_FILTERS.map((option) => ({
    ...option,
    label:
      option.value === "signable"
        ? `${option.label} (${signableCount})`
        : option.value === "not_signable"
          ? `${option.label} (${totalTemplates - signableCount})`
          : option.label,
  }));

  const selectedCategoryObj =
    categories.find((cat) => cat.key === selectedCategory) || null;
  const canDeleteSelectedCategory =
    selectedCategoryObj && Number(selectedCategoryObj.count) === 0;

  const handleCloseCategoryModal = () => {
    setAddCategoryModal(false);
    setCategoryModalMode("add");
    setError("");
    setCategoryName("");
  };

  const handleOpenAddCategoryModal = () => {
    setCategoryModalMode("add");
    setCategoryName("");
    setError("");
    setAddCategoryModal(true);
  };

  const handleOpenEditCategoryModal = () => {
    if (!selectedCategoryObj) {
      handleToastMessage("Select a category before editing.");
      return;
    }
    setCategoryModalMode("edit");
    setCategoryName(selectedCategoryObj.label || "");
    setError("");
    setAddCategoryModal(true);
  };

  const handleCategoryActionClick = ({ key }) => {
    if (key === "add") {
      handleOpenAddCategoryModal();
      return;
    }
    if (key === "edit") {
      handleOpenEditCategoryModal();
      return;
    }
    if (key === "delete" && canDeleteSelectedCategory) {
      setDeleteCategoryModal(selectedCategoryObj);
    }
  };

  const categoryActionItems = [
    { key: "add", label: "Add" },
    { key: "edit", label: "Edit", disabled: !selectedCategoryObj },
    {
      key: "delete",
      label: canDeleteSelectedCategory ? "Delete" : "Delete (empty only)",
      disabled: !canDeleteSelectedCategory,
      danger: true,
    },
  ];

  const handleSaveCategory = () => {
    if (categoryModalMode === "edit") {
      return handleUpdateCategoryUtil({
        categoryName,
        selectedCategoryObj,
        updateCategory,
        loadInitialTemplates,
        handleToastMessage,
        setAddCategoryModal,
        setIsLoading: setisLoading,
        setCategoryName,
        setError,
      });
    }

    return handleCreateCategoryUtil({
      categoryName,
      addNewCategory,
      loadInitialTemplates,
      handleToastMessage,
      setAddCategoryModal,
      setIsLoading: setisLoading,
      setCategoryName,
      setError,
    });
  };
  const handleCategoryClick = async (key) => {
    setSelectedCategory(key);

    if (!templatesByCategory[key]) setisLoading(true);

    try {
      const res = await fetchFileTemplates(key);
      const { active_templates, disabled_templates, categories } =
        res.data.data;

      setTemplatesByCategory((prev) => ({
        ...prev,
        [key]: {
          active: active_templates,
          disabled: disabled_templates,
        },
      }));
      setCategories(categories);
    } catch (error) {
      console.error(`Failed to load templates for ${key}`, error);
      handleToastMessage(`Failed to load templates.`);
    } finally {
      setisLoading(false);
    }
  };

  const handleFileChange = (e) => {
    // This handler is no longer used for direct upload
    // Kept for backward compatibility if needed
    const file = e.target.files?.[0];
    if (!file) return;

    if (!isFileTypeAllowed(file)) {
      console.error("File type invalid");
      handleToastMessage("Only PDF, DOCX and XLSX files are allowed.");
      inputRef.current.value = null;
      return;
    }
    setUploadTemplate(file);
  };

  const handleToastMessage = (message, success = false) => {
    const id = Date.now().toString();

    const newToast = {
      id,
      newMsg: message,
      success,
    };

    setToastMessages([newToast]);
    setShowToast(true);
  };

  const uploadFile = async (file, isLetterheadEnabled = false) => {
    if (!file) return;
    try {
      setUploading(true);

      const formData = new FormData();
      formData.append("file", file);
      formData.append("category", selectedCategory);
      formData.append("is_letterhead_enabled", isLetterheadEnabled);

      await uploadTemplateApi(formData);
      await handleCategoryClick(selectedCategory);

      handleToastMessage("Template uploaded successfully", true);
      setShowUploadModal(false);
    } catch (err) {
      console.error("Upload error:", err);
      handleToastMessage(getUploadErrorMessage(err));
    } finally {
      setUploading(false);
      setUploadTemplate(null);
      if (inputRef.current) inputRef.current.value = null;
    }
  };

  const handleDownloadTemplate = async (template) => {
    try {
      setisLoading(true);
      const res = await downloadTemplateWithMappings(template.formstack_id);
      const blob = res instanceof Blob ? res : new Blob([res.data || res]);
      const isDocx = blob.type?.includes("wordprocessingml") || blob.type?.includes("docx");
      const ext = isDocx ? "docx" : "pdf";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${template.name || "template"} - Field Mappings.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download template", err);
      handleToastMessage("Failed to download template. Try again.");
    } finally {
      setisLoading(false);
    }
  };

  // One editor: the document with its fields outlined, mappings and signature spots.
  const openTemplateStudio = (template) => {
    if (!template?.formstack_id) return;
    const params = new URLSearchParams({
      name: template.name || "",
      type: (template.template_type || "pdf").toLowerCase(),
    });
    window.open(`/settings/templates/${template.formstack_id}?${params}`, "_blank", "noopener,noreferrer");
  };

  const getTemplateHandlers = (template) => ({
    onDisable: () =>
      handleDisableTemplateClick({
        template,
        selectedCategory,
        handleCategoryClick,
        handleToastMessage,
      }),
    onEdit: () => openTemplateStudio(template),
    onPreview: () =>
      handlePreviewUtil({
        template,
        setIsLoading: setisLoading,
        setTemplatePreview,
        setOpenPreview,
        handleToastMessage,
      }),
    showMappingBadge: true,
    onDownload: () => handleDownloadTemplate(template),
    onMoveCategory: () => {
      setMoveDestinationKey("");
      setMoveCategoryModal(template);
    },
    onDelete: () => setDeleteConfirmTemplate(template),
  });

  useEffect(() => {
    const handleScroll = () => {
      if (openDropdownId !== null) {
        setOpenDropdownId(null);
      }
    };

    window.addEventListener("scroll", handleScroll, true);

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [openDropdownId]);

  // Removed automatic upload - now handled by modal
  // useEffect(() => {
  //   if (uploadTemplate) {
  //     uploadFile(uploadTemplate);
  //   }
  // }, [uploadTemplate]);

  const loadInitialTemplates = useCallback(
    async (selectedKeyOverride) => {
      setisLoading(true);
      try {
        const res = await fetchFileTemplates(selectedKeyOverride);
        const { categories, active_templates, disabled_templates } =
          res.data.data;

        setCategories(categories);

        const categoryKeys = new Set((categories || []).map((cat) => cat.key));
        const preferredKey =
          selectedKeyOverride !== undefined
            ? selectedKeyOverride
            : selectedCategory;
        const defaultKey = categoryKeys.has(preferredKey)
          ? preferredKey
          : categories?.[0]?.key || "";
        setSelectedCategory(defaultKey);

        setTemplatesByCategory({
          [defaultKey]: {
            active: active_templates,
            disabled: disabled_templates,
          },
        });
      } catch (error) {
        setShowErrorPage(true);
        console.error("Failed to load templates", error);
      } finally {
        setisLoading(false);
      }
    },
    [selectedCategory]
  );

  const loadPackages = useCallback(async () => {
    try {
      const res = await fetchPackages();
      setPackages(res?.data || []);
    } catch (err) {
      console.error("Failed to load packages", err);
      handleToastMessage(
        err?.response?.status === 403
          ? "You do not have permission to load packages. Please confirm your admin access."
          : "Failed to load packages. Please refresh and try again."
      );
    }
  }, []);

  const loadAllActiveTemplates = useCallback(async () => {
    try {
      const res = await fetchFileTemplates();
      const initialData = res?.data?.data || {};
      const categories = initialData.categories || [];
      const templatesById = new Map();

      const collectActiveTemplates = (templates = []) => {
        templates.forEach((template) => {
          const key = template.formstack_id || template.id || template.name;
          if (key && !templatesById.has(key)) {
            templatesById.set(key, template);
          }
        });
      };

      collectActiveTemplates(initialData.active_templates || []);

      await Promise.all(
        categories.map(async (category) => {
          if (!category?.key) return;
          try {
            const categoryRes = await fetchFileTemplates(category.key);
            collectActiveTemplates(categoryRes?.data?.data?.active_templates || []);
          } catch (categoryErr) {
            console.error(`Failed to load templates for ${category.key}`, categoryErr);
          }
        })
      );

      setAllActiveTemplates(Array.from(templatesById.values()));
    } catch (err) {
      console.error("Failed to load all active templates", err);
    }
  }, []);

  const handleSavePackage = async (data) => {
    setPackageSaving(true);
    try {
      const { sign_template_mapping: signTemplateMapping, ...packagePayload } = data;
      let savedPackage = editingPackage;
      if (editingPackage) {
        const res = await updatePackage(editingPackage.id, packagePayload);
        savedPackage = res?.data || editingPackage;
        handleToastMessage("Package updated successfully", true);
      } else {
        const res = await createPackage(packagePayload);
        savedPackage = res?.data || res;
        handleToastMessage("Package created successfully", true);
      }
      const packageId = savedPackage?.id || editingPackage?.id;
      if (packageId && signTemplateMapping) {
        await saveSignTemplateMappingRules(packageId, signTemplateMapping);
      }
      setPackageModalOpen(false);
      setEditingPackage(null);
      await loadPackages();
    } catch (err) {
      console.error("Failed to save package", err);
      const signErrors = parseSignTemplateMappingApiErrors(err);
      handleToastMessage(
        signErrors.length > 0
          ? signErrors.join(" ")
          : err?.response?.data?.error || "Failed to save package."
      );
    } finally {
      setPackageSaving(false);
    }
  };

  const handleGeneratePackage = async ({ matterId, generatedFolderId }) => {
    if (!packageToGenerate) return;
    setPackageGenerating(true);
    try {
      const payload = { matter_id: matterId };
      if (generatedFolderId) payload.generated_folder_id = generatedFolderId;
      const res = await mergePackage(packageToGenerate.id, payload);
      const result = {
        ...(res?.data || res),
        matter_id: matterId,
        folder_id: (res?.data || res)?.documents?.[0]?.folder || null,
        generated_folder_id: generatedFolderId || null,
        package_id: packageToGenerate.id,
      };
      setPackageMergeResult(result);
      setGeneratePackageModalOpen(false);
      setPackageToGenerate(null);

      const documentCount = result?.documents?.length || 0;
      const errorCount = result?.errors?.length || 0;
      if (documentCount > 0) {
        handleToastMessage(
          `${documentCount} document${documentCount !== 1 ? "s" : ""} generated from package${errorCount ? ` (${errorCount} template error${errorCount !== 1 ? "s" : ""})` : ""}.`,
          true
        );
      } else if (errorCount > 0) {
        handleToastMessage(
          `Package ran, but ${errorCount} template error${errorCount !== 1 ? "s" : ""} prevented document generation.`
        );
      } else {
        handleToastMessage("Package ran, but no documents were generated.");
      }
    } catch (err) {
      console.error("Failed to generate package", err);
      handleToastMessage(getPackageErrorMessage(err));
    } finally {
      setPackageGenerating(false);
    }
  };

  const updateGeneratedDocuments = (updater) => {
    setPackageMergeResult((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        documents: updater(prev.documents || []),
      };
    });
  };

  const handleDownloadGeneratedDocument = async (doc) => {
    const documentId = getDocumentId(doc);
    if (!documentId) return;
    try {
      await downloadDocumentFile({
        ...doc,
        id: documentId,
        name: doc?.name || "generated-document",
      });
    } catch (err) {
      console.error("Failed to download generated document", err);
      handleToastMessage("Failed to download generated document.");
    }
  };

  const handleRenameGeneratedDocument = async (doc, newName) => {
    const documentId = getDocumentId(doc);
    if (!documentId) return;
    setPackageActionBusy(true);
    try {
      const res = await renameGeneratedDocument(documentId, newName);
      const updated = res?.data || { ...doc, name: newName };
      updateGeneratedDocuments((docs) =>
        docs.map((item) =>
          String(getDocumentId(item)) === String(documentId)
            ? { ...item, ...updated, name: updated.name || newName }
            : item
        )
      );
      handleToastMessage("Generated document renamed.", true);
    } catch (err) {
      console.error("Failed to rename generated document", err);
      handleToastMessage(err?.response?.data?.error || "Failed to rename generated document.");
    } finally {
      setPackageActionBusy(false);
    }
  };

  const handleDeleteGeneratedDocuments = async (docs) => {
    setPackageActionBusy(true);
    try {
      await Promise.all(
        docs.map((doc) => deleteGeneratedDocument(getDocumentId(doc)))
      );
      const deletedIds = new Set(docs.map((doc) => String(getDocumentId(doc))));
      updateGeneratedDocuments((items) =>
        items.filter((item) => !deletedIds.has(String(getDocumentId(item))))
      );
      handleToastMessage("Generated document deleted.", true);
    } catch (err) {
      console.error("Failed to delete generated documents", err);
      handleToastMessage("Failed to delete generated document.");
    } finally {
      setPackageActionBusy(false);
    }
  };

  const handleCombineGeneratedDocuments = async (docs, outputName) => {
    if (!packageMergeResult?.package_id || !packageMergeResult?.matter_id) return;
    setPackageActionBusy(true);
    try {
      const res = await combinePackageForSigning(packageMergeResult.package_id, {
        matter_id: packageMergeResult.matter_id,
        folder_id: packageMergeResult.folder_id,
        document_ids: docs.map((doc) => getDocumentId(doc)),
        output_name: outputName,
      });
      const result = res?.data || {};
      const combinedDocument = result.document;
      if (combinedDocument) {
        updateGeneratedDocuments((items) => [...items, combinedDocument]);
      }
      handleToastMessage("Combined PDF created.", true);
    } catch (err) {
      console.error("Failed to combine generated documents", err);
      handleToastMessage(err?.response?.data?.error || "Failed to combine generated documents.");
    } finally {
      setPackageActionBusy(false);
    }
  };

  const handleDeletePackage = async () => {
    if (!deleteConfirmPackage) return;
    setisLoading(true);
    try {
      await deletePackageApi(deleteConfirmPackage.id);
      setDeleteConfirmPackage(null);
      handleToastMessage("Package deleted successfully", true);
      await loadPackages();
    } catch (err) {
      console.error("Failed to delete package", err);
      handleToastMessage("Failed to delete package.");
    } finally {
      setisLoading(false);
    }
  };

  useEffect(() => {
    setisLoading(true);
    loadInitialTemplates();
    loadPackages();
  }, []);

  return showErrorPage ? (
    <ErrorPageContainer>
      <div>
        <ErrorPageIcon />
      </div>
      <div>
        Unable to load data. Please refresh the page or try again later.
      </div>
    </ErrorPageContainer>
  ) : (
    <Container>
      <Sidebar>
        <Header>
          <CategoryHeader>Template Category</CategoryHeader>
          <Dropdown
            menu={{
              items: categoryActionItems,
              onClick: handleCategoryActionClick,
            }}
            placement="bottomRight"
            trigger={["click"]}
          >
            <AddCategoryButton
              data-testid="add-category-btn"
              onClick={(event) => event.preventDefault()}
            >
              <AddCategoryIcon />
            </AddCategoryButton>
          </Dropdown>
        </Header>

        <CategoryList>
          {categories.map((cat) => (
            <button
              key={cat.key}
              id={cat.key}
              onClick={() => handleCategoryClick(cat.key)}
              className={selectedCategory === cat.key ? "active" : ""}
            >
              <Tooltip
                placement="right"
                title={cat.label}
                align={{
                  offset: [10, 0],
                }}
              >
                <span className="label">{cat.label}</span>
              </Tooltip>

              <span className="count">({cat.count})</span>
            </button>
          ))}
        </CategoryList>
      </Sidebar>

      <TemplateContainer>
        <Header>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <ViewToggle>
              <ToggleBtn $active={viewMode === "templates"} onClick={() => setViewMode("templates")}>
                Templates
              </ToggleBtn>
              <ToggleBtn $active={viewMode === "packages"} onClick={() => setViewMode("packages")}>
                Packages ({packages.length})
              </ToggleBtn>
            </ViewToggle>
            {viewMode === "templates" && (
              <>
                <span>
                  {categories.find((c) => c.key === selectedCategory)?.label || ""} (
                  {totalTemplates})
                </span>
                {totalTemplates > 0 && (
                  <Segmented
                    size="small"
                    data-testid="template-signature-filter"
                    options={signatureFilterOptions}
                    value={signatureFilter}
                    onChange={setSignatureFilter}
                  />
                )}
              </>
            )}
            {viewMode === "packages" && (
              <Button
                variant="contained"
                onClick={() => {
                  setEditingPackage(null);
                  loadAllActiveTemplates();
                  setPackageModalOpen(true);
                }}
                sx={CreatePackageButtonStyles}
              >
                Create Package
              </Button>
            )}
          </div>
          {viewMode === "templates" && (
            <>
              <input
                ref={inputRef}
                type="file"
                data-testid="file-upload-input"
                accept={acceptList}
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
              <Button
                variant="contained"
                endIcon={<UploadIcon />}
                disabled={uploading}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowUploadModal(true);
                }}
                sx={UploadButtonStyles}
              >
                {uploading ? "Uploading..." : "Upload"}
              </Button>
            </>
          )}
        </Header>

        {viewMode === "templates" ? (
          <TemplatesGrid>
            <div className="scroll-wrapper">
              {activeTemplates?.length === 0 &&
                disabledTemplates.length === 0 && (
                  <EmptyState
                    icon={NoTemplatesIcon}
                    message={
                      totalTemplates === 0
                        ? "No Templates Added"
                        : signatureFilter === "signable"
                          ? "No signable templates in this category yet. Open a template and add signature spots."
                          : "Every template in this category is signable."
                    }
                  />
                )}

              {activeTemplates.length > 0 && (
                <div className="section">
                  <SectionTitle>Active Templates</SectionTitle>
                  <TemplateGrid>
                    {activeTemplates.map((template) => (
                      <TemplateCardItem
                        key={template.id}
                        isEnabled={template?.is_active}
                        template={template}
                        openDropdownId={openDropdownId}
                        setOpenDropdownId={setOpenDropdownId}
                        {...getTemplateHandlers(template)}
                      />
                    ))}
                  </TemplateGrid>
                </div>
              )}

              {disabledTemplates.length > 0 && (
                <div className="section">
                  <SectionTitle>Disabled Templates</SectionTitle>
                  <TemplateGrid>
                    {disabledTemplates.map((template) => (
                      <TemplateCardItem
                        key={template.id}
                        template={template}
                        openDropdownId={openDropdownId}
                        setOpenDropdownId={setOpenDropdownId}
                        {...getTemplateHandlers(template)}
                      />
                    ))}
                  </TemplateGrid>
                </div>
              )}
            </div>
          </TemplatesGrid>
        ) : (
          <TemplatesGrid>
            <div className="scroll-wrapper">
              {packages.length === 0 && (
                <EmptyState
                  icon={NoTemplatesIcon}
                  message="No Packages Created"
                />
              )}
              <PackageList>
                {packages.map((pkg) => (
                  <PackageCard key={pkg.id}>
                    <FolderGlyph aria-hidden="true" />
                    <PackageText>
                      <PackageName title={pkg.name}>{pkg.name}</PackageName>
                      <PackageMeta>
                        {pkg.template_count} template{pkg.template_count !== 1 ? "s" : ""}
                      </PackageMeta>
                    </PackageText>
                    <Dropdown
                      trigger={["click"]}
                      menu={{
                        items: [
                          { key: "generate", label: "Generate" },
                          { key: "edit", label: "Edit" },
                          { key: "delete", label: "Delete", danger: true },
                        ],
                        onClick: ({ key }) => {
                          if (key === "generate") {
                            setPackageToGenerate(pkg);
                            setGeneratePackageModalOpen(true);
                          }
                          if (key === "edit") {
                            setEditingPackage(pkg);
                            loadAllActiveTemplates();
                            setPackageModalOpen(true);
                          }
                          if (key === "delete") {
                            setDeleteConfirmPackage(pkg);
                          }
                        },
                      }}
                    >
                      <PackageMenuButton type="button" aria-label={`Actions for ${pkg.name}`}>
                        <MenuDots aria-hidden="true">
                          <span />
                          <span />
                          <span />
                        </MenuDots>
                      </PackageMenuButton>
                    </Dropdown>
                  </PackageCard>
                ))}
              </PackageList>
            </div>
          </TemplatesGrid>
        )}
      </TemplateContainer>
      {openPreview && (
        <PrintPreview
          printData={templatePreview}
          setOpenPreview={setOpenPreview}
        />
      )}
      {addCategoryModal && (
        <AddCategoryModal
          isOpen={addCategoryModal}
          categoryName={categoryName}
          header={
            categoryModalMode === "edit"
              ? "Edit Template Category"
              : "Create new Template Category"
          }
          confirmLabel={categoryModalMode === "edit" ? "Save" : "Create"}
          setCategoryName={(value) => {
            setCategoryName(value);
            if (error) setError(""); // clear error on typing
          }}
          error={error}
          onCancel={handleCloseCategoryModal}
          onCreate={handleSaveCategory}
        />
      )}
      <UploadTemplateModal
        open={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUpload={uploadFile}
        uploading={uploading}
        handleError={(message) => handleToastMessage(message)}
      />
      <Modal
        title="Delete Template"
        open={!!deleteConfirmTemplate}
        onOk={async () => {
          const template = deleteConfirmTemplate;
          setDeleteConfirmTemplate(null);
          await handleDeleteTemplateUtil({
            template,
            selectedCategory,
            handleCategoryClick,
            handleToastMessage,
            setIsLoading: setisLoading,
          });
        }}
        onCancel={() => setDeleteConfirmTemplate(null)}
        okText="Delete"
        okButtonProps={{ danger: true }}
        cancelText="Cancel"
      >
        <p>
          Are you sure you want to permanently delete{" "}
          <strong>{deleteConfirmTemplate?.name}</strong>? This action cannot be
          undone.
        </p>
      </Modal>
      <Modal
        title="Move to another Category"
        open={!!moveCategoryModal}
        onOk={() =>
          handleMoveTemplateCategoryUtil({
            template: moveCategoryModal,
            destinationCategoryCode: moveDestinationKey,
            selectedCategory,
            handleCategoryClick,
            handleToastMessage,
            setIsLoading: setisLoading,
            setMoveCategoryModal,
            setTemplatesByCategory,
          })
        }
        onCancel={() => {
          setMoveCategoryModal(null);
          setMoveDestinationKey("");
        }}
        okText="Move"
        okButtonProps={{ disabled: !moveDestinationKey }}
        cancelText="Cancel"
        confirmLoading={isLoading}
        destroyOnClose
      >
        <p style={{ marginBottom: 12 }}>
          Move <strong>{moveCategoryModal?.name}</strong> to another category so
          you can empty a category before deleting it.
        </p>
        <Select
          style={{ width: "100%" }}
          placeholder="Select destination category"
          value={moveDestinationKey || undefined}
          onChange={(value) => setMoveDestinationKey(value)}
          options={categories
            .filter((cat) => cat.key !== selectedCategory)
            .map((cat) => ({
              value: cat.key,
              label: cat.label,
            }))}
          aria-label="Destination category"
        />
      </Modal>
      <Modal
        title="Delete Category"
        open={!!deleteCategoryModal}
        onOk={() =>
          handleDeleteCategoryUtil({
            selectedCategoryObj: deleteCategoryModal,
            deleteCategory,
            loadInitialTemplates,
            handleToastMessage,
            setIsLoading: setisLoading,
            setDeleteCategoryModal,
          })
        }
        onCancel={() => setDeleteCategoryModal(null)}
        okText="Delete"
        okButtonProps={{ danger: true }}
        cancelText="Cancel"
        confirmLoading={isLoading}
      >
        <p>
          Are you sure you want to delete{" "}
          <strong>{deleteCategoryModal?.label}</strong>? This action cannot be
          undone.
        </p>
      </Modal>
      <PackageModal
        open={packageModalOpen}
        onClose={() => {
          setPackageModalOpen(false);
          setEditingPackage(null);
        }}
        onSave={handleSavePackage}
        editingPackage={editingPackage}
        allTemplates={allActiveTemplates}
        saving={packageSaving}
      />
      <PackageGenerateModal
        open={generatePackageModalOpen}
        pkg={packageToGenerate}
        generating={packageGenerating}
        onClose={() => {
          if (packageGenerating) return;
          setGeneratePackageModalOpen(false);
          setPackageToGenerate(null);
        }}
        onGenerate={handleGeneratePackage}
      />
      <PackageResultsModal
        open={!!packageMergeResult}
        result={packageMergeResult}
        busy={packageActionBusy}
        onClose={() => setPackageMergeResult(null)}
        onDownload={handleDownloadGeneratedDocument}
        onDelete={handleDeleteGeneratedDocuments}
        onRename={handleRenameGeneratedDocument}
        onCombine={handleCombineGeneratedDocuments}
      />
      <Modal
        title="Delete Package"
        open={!!deleteConfirmPackage}
        onOk={handleDeletePackage}
        onCancel={() => setDeleteConfirmPackage(null)}
        okText="Delete"
        okButtonProps={{ danger: true }}
        cancelText="Cancel"
      >
        <p>
          Are you sure you want to permanently delete the package{" "}
          <strong>{deleteConfirmPackage?.name}</strong>? The templates inside
          will not be deleted.
        </p>
      </Modal>
      {isLoading && <Loader />}
      {showToast && (
        <ErrorToast
          message={toastMessages}
          onClose={(id) =>
            setToastMessages((prev) => {
              const filtered = prev.filter((msg) => msg.id !== id);
              if (filtered.length === 0) setShowToast(false);
              return filtered;
            })
          }
        />
      )}
    </Container>
  );
};

export default FileTemplates;
